import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiHome, FiUsers, FiPackage, FiShoppingCart,
  FiSettings, FiLogOut, FiMenu, FiX, FiMapPin, FiLayers,
  FiUser, FiLock, FiChevronDown, FiFileText, FiBookOpen
} from 'react-icons/fi';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = React.useMemo(() => {
    const items = [
      { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
    ];

    if (user?.role === 'superadmin') {
      items.push({ path: '/resellers', icon: FiUsers, label: 'Resellers' });
    }

    if (user?.role === 'reseller') {
      items.push(
        { path: '/customers', icon: FiUsers, label: 'Merchants' },
        { path: '/plans', icon: FiLayers, label: 'Plans' },
        { path: '/news', icon: FiFileText, label: 'News' },
        { path: '/how-to-use', icon: FiBookOpen, label: 'How to Use' }
      );
    }

    if (user?.role === 'customer') {
      items.push({ path: '/branches', icon: FiMapPin, label: 'Branches' });
    }

    if (['branch_manager', 'cashier', 'staff'].includes(user?.role)) {
      items.push(
        { path: '/products', icon: FiPackage, label: 'Products' },
        { path: '/pos', icon: FiShoppingCart, label: 'POS' }
      );
    }

    items.push({ path: '/settings', icon: FiSettings, label: 'Settings' });

    return items;
  }, [user]);

  const roleLabels = {
    superadmin: 'Super Admin',
    reseller: 'Reseller',
    customer: 'Merchant',
    branch_manager: 'Branch Manager',
    cashier: 'Cashier',
    staff: 'Staff',
  };

  const initials = user?.fullName
    ?.split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed lg:translate-x-0 lg:static inset-y-0 left-0 z-50 w-60 bg-slate-900 flex flex-col transition-transform duration-300 ease-in-out`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-slate-700/50">
          <div className="flex items-center gap-2.5">
            <img src="/icon-192x192.png" alt="AIVAPOS Logo" className="h-8 w-8 rounded-lg" />
            <span className="text-white text-lg tracking-wide" style={{ fontFamily: '"Saved By Zero", Inter, sans-serif', letterSpacing: '0.05em' }}>AIVAPOS</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-white">
            <FiX size={20} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <item.icon size={17} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* User + Logout */}
        <div className="p-3 border-t border-slate-700/50">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.fullName}</p>
              <p className="text-xs text-slate-400">{roleLabels[user?.role] || user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-all"
          >
            <FiLogOut size={17} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-6 flex-shrink-0 relative">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700 p-1"
          >
            <FiMenu size={22} />
          </button>
          <div className="hidden lg:block">
            <p className="text-sm font-semibold text-gray-800">
              {menuItems.find((m) => m.path === location.pathname)?.label || 'Dashboard'}
            </p>
          </div>
          <div className="flex items-center gap-3 ml-auto" ref={dropdownRef}>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-800 leading-tight">{user?.fullName}</p>
              <p className="text-xs text-gray-400">{roleLabels[user?.role] || user?.role}</p>
            </div>
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center gap-1.5 focus:outline-none"
            >
              <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                {initials}
              </div>
              <FiChevronDown size={14} className={`text-gray-500 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="absolute right-4 top-14 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                <div className="px-4 py-2.5 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-800 truncate">{user?.fullName}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={() => { setDropdownOpen(false); navigate('/profile'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FiUser size={15} className="text-gray-400" />
                  My Profile
                </button>
                <button
                  onClick={() => { setDropdownOpen(false); navigate('/change-password'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FiLock size={15} className="text-gray-400" />
                  Change Password
                </button>
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={() => { setDropdownOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <FiLogOut size={15} />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>

      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black/40 z-40 lg:hidden" />
      )}
    </div>
  );
};

export default Layout;
